import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  BillingSettings,
  BillingSettingsPayload,
  CompanySettingsResponse,
  CreateEmbedConfigurationPayload,
  EmbedConfiguration,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  PaginationQuery,
  Response,
  SaveCompanySettingResponse,
  SortByQuery,
  UpdateEmbedConfigurationPayload,
} from "~/types/api";

export function createSettingsApi({
  getRequest,
  postRequest,
  putRequest,
  patchRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches all company settings for the current organization. */
  const companySettings = () => getRequest<CompanySettingsResponse>("/company-settings");

  /** Stores or updates a single company setting by key. */
  const storeCompanySetting = (data: { key: string; value: string }) =>
    postRequest<SaveCompanySettingResponse>("/company-settings", data);

  /** Deletes a single company setting by key. */
  const deleteCompanySetting = (key: string) =>
    deleteRequest<SaveCompanySettingResponse>(`/company-settings/${encodeURIComponent(key)}`);

  /** Fetches the billing settings for the current organization. */
  const billingSettings = () => getRequest<Response<BillingSettings>>("/billing/settings");

  /** Updates the billing settings for the current organization. */
  const updateBillingSettings = (data: BillingSettingsPayload) =>
    putRequest<Response<BillingSettings>>(`/billing/settings`, data);

  /** Fetches a paginated list of embed configurations. */
  const embedConfigurations = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) =>
    getPaginationRequest<EmbedConfiguration>(
      "/company-settings/embed-configurations",
      pagination,
      sortBy,
      includes,
      filterBy,
    );

  /** Fetches a single embed configuration by ID. */
  const embedConfiguration = (id: string) =>
    getRequest<Response<EmbedConfiguration>>(`/company-settings/embed-configurations/${id}`);

  /** Creates a new embed configuration. */
  const createEmbedConfiguration = (data: CreateEmbedConfigurationPayload) =>
    postRequest<Response<EmbedConfiguration>>("/company-settings/embed-configurations", data);

  /** Updates an existing embed configuration. */
  const updateEmbedConfiguration = (id: number, data: UpdateEmbedConfigurationPayload) =>
    patchRequest<Response<EmbedConfiguration>>(`/company-settings/embed-configurations/${id}`, data);

  /** Deletes an embed configuration. */
  const deleteEmbedConfiguration = (id: number) =>
    deleteRequest<EmptyResponse>(`/company-settings/embed-configurations/${id}`);

  /** Fetches the generated embed code snippet for a configuration. */
  const getEmbedCode = (id: number) =>
    getRequest<Response<{ code: string }>>(`/company-settings/embed-configurations/${id}/embed-code`);

  return {
    companySettings,
    storeCompanySetting,
    deleteCompanySetting,
    billingSettings,
    updateBillingSettings,
    embedConfigurations,
    embedConfiguration,
    createEmbedConfiguration,
    updateEmbedConfiguration,
    deleteEmbedConfiguration,
    getEmbedCode,
  };
}

export type SettingsApi = ReturnType<typeof createSettingsApi>;
